import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for dynamic meta tags on each page
 * Usage: <SEO title="Page Title" description="Page description" />
 */
const SEO = ({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = 'website',
  author = 'Kioo Radio 98.1 FM',
  publishedTime,
  modifiedTime,
  section,
  noindex = false
}) => {
  const siteTitle = 'Kioo Radio 98.1 FM';
  const defaultDescription = 'Christian radio broadcasting faith, hope and love to Liberia, Sierra Leone and Guinea from Foya, Lofa County.';
  const defaultImage = 'https://customer-assets.emergentagent.com/job_2b745d9e-4914-42ba-a105-f86fbb865919/artifacts/vc9slfaz_KIOO%20RADIO.jpg';
  const siteUrl = 'https://kiooradio.org';
  const defaultKeywords = 'Kioo Radio, 98.1 FM, Christian radio, Liberia, Sierra Leone, Guinea, Gospel, Faith';

  const fullTitle = title ? `${title} | ${siteTitle}` : `${siteTitle} - The Gift of Good News`;
  const metaDescription = description || defaultDescription;
  const metaImage = image || defaultImage;
  const metaUrl = url ? `${siteUrl}${url}` : siteUrl;
  const metaKeywords = keywords || defaultKeywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content={author} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={metaUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific (for news/blog) */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {section && <meta property="article:section" content={section} />}
          <meta property="article:author" content={author} />
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={metaUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      
      {/* Additional SEO */}
      <meta name="geo.region" content="LR" />
      <meta name="geo.placename" content="Foya, Lofa County, Liberia" />
    </Helmet>
  );
};

// Predefined SEO configurations for main pages
export const PageSEO = {
  home: {
    title: null, // Uses default
    description: 'Kioo Radio 98.1 FM - The Gift of Good News. Christian radio broadcasting faith, hope and love to Liberia, Sierra Leone and Guinea from Foya, Lofa County.',
    keywords: 'Kioo Radio, 98.1 FM, Christian radio Liberia, Gospel radio West Africa, faith-based programs, Makona River Region'
  },
  about: {
    title: 'About Us',
    description: 'Learn about Kioo Radio 98.1 FM, our mission to broadcast faith, hope and love across the Makona River Region. Serving Liberia, Sierra Leone and Guinea since 2018.',
    keywords: 'Kioo Radio history, Christian radio mission, Makona River Region broadcasting, Foya Liberia radio station'
  },
  programs: {
    title: 'Radio Programs',
    description: 'Explore our faith-based radio programs including Bible teaching, gospel music, prayer time, and inspirational content. Broadcasting daily in English and French.',
    keywords: 'Christian radio programs, Bible teaching, gospel music, prayer ministry, Guidelines for Living, Truth for Life'
  },
  news: {
    title: 'News & Updates',
    description: 'Latest news and updates from Kioo Radio 98.1 FM. Stay informed about our programs, events, and community impact across the Makona River Region.',
    keywords: 'Kioo Radio news, Christian radio updates, Liberia news, community updates'
  },
  gallery: {
    title: 'Photo Gallery',
    description: 'View photos from Kioo Radio 98.1 FM events, studio, and community outreach. See the impact of Christian broadcasting in West Africa.',
    keywords: 'Kioo Radio photos, radio station gallery, community events Liberia'
  },
  donate: {
    title: 'Support Our Mission',
    description: 'Support Kioo Radio 98.1 FM with your donation. Help us continue broadcasting faith, hope and love to communities across Liberia, Sierra Leone and Guinea.',
    keywords: 'donate to Christian radio, support Kioo Radio, charitable giving Liberia'
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with Kioo Radio 98.1 FM. Contact us for prayer requests, program inquiries, or partnership opportunities.',
    keywords: 'contact Kioo Radio, radio station contact, prayer requests'
  },
  family: {
    title: 'Join the Kioo Family',
    description: 'Join the Kioo Radio family! Sign up to receive updates, birthday greetings, and stay connected with our community of listeners.',
    keywords: 'join Kioo Radio, listener community, radio family'
  }
};

export default SEO;
